import CallTypes from './call.types';
import CallState from './call.state';
import AnyAction from 'store/anyActions';
import { formatPhoneNumber } from 'utils';
import { createConnection, getCallDirection, getMonitorInfo } from './call.utils';

export default function (state: CallState | null = null, action: AnyAction): CallState | null {
    switch (action.type) {
        case CallTypes.SET_CONNECTIONS: {
            if (!state) return null;

            const { connections } = action.payload;

            return {
                ...state,
                connections,
            };
        }
        case CallTypes.INCOMING_CALL:
            return {
                incoming: true,
                connections: [
                    {
                        id: action.payload.id,
                        number: action.payload.number,
                        activeConnection: true,
                        initialConnection: true,
                        status: 'INCOMING',
                    },
                ],
                direction: action.payload.direction,
                number: action.payload.number,
                monitorInfo: {
                    status: undefined,
                    isEnhancedMonitoringEnabled: false,
                },
            };
        case CallTypes.INITIATE_LEGACY_MONITORING: {
            const { contact } = action.payload;

            const initialConnection = contact.getInitialConnection() as connect.VoiceConnection;
            const agentConnection = contact.getAgentConnection() as connect.VoiceConnection;
            const activeInitialConnection = contact.getActiveInitialConnection() as connect.VoiceConnection;

            return {
                ...state!,
                monitorInfo: getMonitorInfo(agentConnection),
                inCall: true,
                connections: [
                    {
                        id: action.payload.contact.contactId,
                        contact: action.payload.contact,
                        activeConnection: true,
                        initialConnection: true,
                        number: activeInitialConnection?.getEndpoint().phoneNumber,
                        status: 'CONNECTED',
                        start: new Date(),
                    },
                ],
                direction: contact.isInbound() ? 'inbound' : 'outbound',
                number: initialConnection.getEndpoint().phoneNumber,
            };
        }
        case CallTypes.INITIATE_ENHANCED_MONITORING:
            const { contact, originalContact, originalAgent } = action.payload;
            const connections = contact.getConnections() as connect.VoiceConnection[];
            const agentConnection = contact.getAgentConnection() as connect.VoiceConnection;

            const { AgentInfo, InitiationMethod, InitiationTimestamp } = originalContact;

            const callDirection = getCallDirection(InitiationMethod);

            let timestamp: string | undefined;

            switch (callDirection) {
                case 'inbound':
                    timestamp = InitiationTimestamp;
                    break;
                case 'outbound':
                    timestamp = AgentInfo?.ConnectedToAgentTimestamp;
                    break;
            }

            return {
                connections: connections.map(connection =>
                    createConnection(connection, originalContact, originalAgent)
                ),
                inCall: true,
                start: new Date(timestamp ?? Date.now()),
                monitorInfo: getMonitorInfo(agentConnection),
                direction: callDirection,
                number: contact.getInitialConnection().getEndpoint().phoneNumber,
            };
		case CallTypes.CLEAR_CONNECT_CONTACT:
			state?.connections.map(c => c.activeConnection).cl
            return state!;
        case CallTypes.CALLBACK_CALL:
            return {
                callback: true,
                incoming: true,
                connections: [
                    {
                        id: action.payload.id,
                        number: action.payload.number,
                        activeConnection: true,
                        initialConnection: true,
                        status: 'CALLBACK',
                    },
                ],
                direction: 'callback',
                number: action.payload.number,
                monitorInfo: {
                    status: undefined,
                    isEnhancedMonitoringEnabled: false,
                },
            };
        case CallTypes.ACCEPT_CALL:
            return { ...state!, incoming: false };
        case CallTypes.CONNECTION_STARTED:
            return {
                ...state!,
                incoming: false,
                connections: state
                    ? state.connections.map(c =>
                          c.id === action.payload ? { ...c, status: 'CONNECTED', start: c.start || new Date() } : c
                      )
                    : [],
                inCall: true,
                start: new Date(),
            };
        case CallTypes.CONNECTION_CONNECTING:
            //If we have a quick hangup this action still fires after the call has finished
            if (!state) return state;
            return {
                ...state,
                connections: state.connections.map(c => (c.id === action.payload ? { ...c, status: 'CONNECTING' } : c)),
            };
        case CallTypes.UPDATE_CALL_STATUS: {
            return {
                ...state!,
                connections: state!.connections.map(c =>
                    c.id === action.payload.connectionId ? { ...c, status: action.payload.status } : c
                ),
            };
        }
        case CallTypes.OUTBOUND_CALL:
            return {
                ...state!,
                connections: [
                    {
                        id: action.payload.id,
                        number: formatPhoneNumber(action.payload.number),
                        originNumber: action.payload.number,
                        status: 'RINGING',
                        activeConnection: true,
                        initialConnection: true,
                    },
                ],
            };
        case CallTypes.MAKE_CALL:
            return {
                ...state!,
                number: action.payload.number!,
                direction: 'outbound',
                // eslint-disable-next-line
                connections: (state && state.connections) || [],
                outbound: true,
            };
        case CallTypes.SET_HOLD:
            if (!state) return null;
            const { connectionId, hold } = action.payload;
            return {
                ...state!,
                connections: state!.connections.map(c => (c.id === connectionId ? { ...c, hold } : c)),
            };
        case CallTypes.ADD_CONNECTION:
            return {
                ...state!,
                connections: [
                    ...state!.connections.map(c => {
                        return { ...c, activeConnection: false };
                    }),
                    {
                        ...action.payload,
                        status: 'RINGING',
                        activeConnection: true,
                        originNumber: action.payload.number!,
                    },
                ],
            };
        case CallTypes.TOGGLE_CONNECTIONS:
            if (!state || !state.connections.length) return state;
            return {
                ...state,
                connections: state.connections.map(c =>
                    c.activeConnection
                        ? { ...c, activeConnection: false, hold: true }
                        : { ...c, activeConnection: true, hold: false }
                ),
            };
        case CallTypes.END_CONNECTION:
            if (!state || !state.connections) return state;
            //If call hasn't started then just clear
            if (!state.start) return null;
            return {
                ...state!,
                conferenced: false,
                connections: state!.connections
                    .filter(c => c.id !== action.payload)
                    .map(c => ({ ...c, activeConnection: true })),
            };
        // case CallTypes.SET_SENSITIVE:
        //     return { ...state!, sensitive: action.payload.sensitive };
        case CallTypes.CONFERENCE_CONNECTIONS:
            return {
                ...state!,
                connections: state!.connections.map(c => ({
                    ...c,
                    activeConnection: true,
                    hold: false,
                })),
                conferenced: true,
            };
        case CallTypes.RECOVER_CALL:
            return action.payload;
        case CallTypes.MUTE:
            if (!state) return state;
            return { ...state, muted: true };
        case CallTypes.UNMUTE:
            if (!state) return state;
            return { ...state!, muted: false };
        case CallTypes.CALL_ENDED:
            return null;
        case CallTypes.PARK_CALL_CONTACT_TRANSFER:
            return {
                ...state!,
                parking: true,
            };
        case CallTypes.SET_MONITOR_STATUS:
            if (!state) return null;

            const { status } = action.payload;

            return {
                ...state,
                monitorInfo: { ...state.monitorInfo, status },
            };
        default:
            return state;
    }
}
